import {
  ArrowRightIcon,
  Backdrop,
  CheckMarkIconTwoTone,
  ClockCircleIcon,
  DeclineIconTwoTone,
  Typography,
  useNotificationModal,
} from '@room-match/shared-ui-components';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { COLORS } from 'shared/constants/COLORS';
import { ROUTES } from 'shared/constants/ROUTES';
import { CustomTypography } from 'shared/elements/CustomTypography';
import { AuditionStatus } from 'shared/enums/AuditionStatus';
import { SelftapesSteps } from 'shared/enums/SelftapesSteps';
import { IAuditionSchedulesAttributes } from 'shared/interfaces/IAuditionSchedule';
import { auditionService } from 'shared/services/auditionService';
import { getFormalDateShort } from 'shared/utils/dateUtils';
import { ls } from 'shared/utils/ls';
import { timeParser } from 'shared/utils/timeHelpers';
import { CustomIconButton, Left, MainContainer, Right } from './PastSelfTapesItem.styles';
import { CustomTooltip } from 'pages/Opportunities/components/CustomTooltip/CustomTooltip';
const { PRIMARY_TIME, LATE_LONG_COLOR, WARNING_TAB_BORDER } = COLORS;

type Props = {
  data: IAuditionSchedulesAttributes;
  confirmed?: boolean;
  status: string;
};
const { getAudition } = auditionService();
const PastSelfTapesItem: React.FC<Props> = ({ data, confirmed, status }) => {
  const [audID, setaudID] = useState('');
  const { data: audData, isLoading } = getAudition(audID);
  const history = useHistory();
  const { setLS } = ls();
  const { handleOpen, isOpen, NotificationModal, handleSetMessage, handleChangeModalType } = useNotificationModal({
    type: 'warning',
  });

  useEffect(() => {
    if (!isLoading && audData?.data && audID !== '') {
      if (!audData.data.attributes.audition.data.attributes.casting_role.data.attributes.self_tapes_requirements) {
        handleChangeModalType('error');
        handleSetMessage(
          'Currently Unavailable',
          `You are unable to submit a self tape at this time as no requirements have been set yet by the casting director, please check with your agency or the casting contact directly.`,
          'OK',
        );
        handleOpen();
        return;
      }
      setLS('auditionData', JSON.stringify(audData?.data));
      history.push(`${ROUTES.TALENT.SELFTAPES}/${data.audition.casting_role.project.id}`);
    }
  }, [audData, isLoading, audID]);

  const onClick = () => {
    const { self_tapes_requirements, project, id, name } = data.audition.casting_role;
    if (confirmed) {
      // setaudID(data.id);
      history.push(
        `${ROUTES.TALENT.SELFTAPES}/${data.audition.casting_role.project.id}/${data.id}/${SelftapesSteps.SIZE_CARD}`,
      );
    } else {
      history.push(`${ROUTES.TALENT.PAST_SELFTAPES}/${data.id}`);
    }
  };

  const due = () =>
    timeParser(
      data.audition.casting_role.self_tapes_requirements?.date || '',
      data.audition.casting_role.self_tapes_requirements?.time || '',
      data.audition.casting_role.self_tapes_requirements?.timezone || '',
    );

  const isRevision = data?.self_tapes_requirement_revision;

  if (isLoading) return <Backdrop isLoading={true} />;

  return (
    <div style={{ position: 'relative', width: '100%', height: 'fit-content' }}>
      {isRevision ? (
        <div
          style={{
            position: 'absolute',
            background: WARNING_TAB_BORDER,
            width: 'fit-content',
            // marginLeft: 10,
            top: -20,
            left: 10,
            borderTopLeftRadius: 5,
            borderTopRightRadius: 5,
          }}
        >
          <CustomTypography fontSize={14} color="white" style={{ padding: '0px 15px' }}>
            ST Revision
          </CustomTypography>
        </div>
      ) : null}
      <MainContainer isRevision={isRevision} onClick={onClick}>
        {confirmed ? (
          <Left>
            <ClockCircleIcon htmlColor={due().includes('D') ? PRIMARY_TIME : LATE_LONG_COLOR} />
            <CustomTypography fontSize={15}>{due()}</CustomTypography>
            <CustomTypography fontSize={12}>Due Time</CustomTypography>
          </Left>
        ) : (
          <Left>
            {status === AuditionStatus.REJECTED ? (
              <React.Fragment>
                <DeclineIconTwoTone />
                <CustomTypography fontSize={14}>Rejected</CustomTypography>
                <CustomTypography fontSize={12}>
                  {data.updated_at ? getFormalDateShort(new Date(data.checked_in_at)) : '–'}
                </CustomTypography>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <CheckMarkIconTwoTone />
                <CustomTypography fontSize={14}>Submitted</CustomTypography>
                <CustomTypography fontSize={12}>
                  {data.checked_in_at ? getFormalDateShort(new Date(data.checked_in_at)) : '–'}
                </CustomTypography>
              </React.Fragment>
            )}

            {status === AuditionStatus.HIDDEN ||
            status === AuditionStatus.APPROVED ||
            status === AuditionStatus.CONFIRMED ? (
              <Typography fontSize={10} color="primary">
                Viewed
              </Typography>
            ) : (
              <React.Fragment />
            )}
          </Left>
        )}
        <Right>
          <CustomTypography fontSize={16}>{data.audition.casting_role.project.name || '–'}</CustomTypography>

          <CustomTypography fontSize={14}>{data.audition.casting_role.name || ''}</CustomTypography>

          <CustomTypography fontSize={14}>
            {data.audition.profile.agency
              ? data.audition.profile.agency.state && data.audition.profile.agency.country
                ? `${data.audition.profile.agency.state}, ${data.audition.profile.agency.country}`
                : data.audition.profile.agency.state
                  ? data.audition.profile.agency.state
                  : data.audition.profile.agency.country
                    ? data.audition.profile.agency.country
                    : ''
              : ''}
          </CustomTypography>

          <Typography fontSize={14} color="primary">
            {data.audition.profile.agency && data.audition.profile.agency.name ? (
              <CustomTooltip title={data.audition.profile.agency.name}>
                <Typography
                  color="primary"
                  style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  fontSize={14}
                >
                  {data.audition.profile.agency.name}
                </Typography>
              </CustomTooltip>
            ) : (
              <Typography color="primary" fontSize={14}>
                Non Represented
              </Typography>
            )}
          </Typography>
        </Right>
        <CustomIconButton>
          <ArrowRightIcon />
        </CustomIconButton>
        {isOpen && (
          <NotificationModal
            onClose={() => {
              history.push(ROUTES.TALENT.SELFTAPES);
            }}
          />
        )}
      </MainContainer>
    </div>
  );
};

export default PastSelfTapesItem;
